import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { ReactComponent as SearchIcon } from 'icons/icon-search.svg'

import { useDebounce } from 'hooks'

import {
  useCurrentUser,
  useSearchMentionableUsersQuery,
} from 'store/api/yay/user'
import { User } from 'lib/_types'

import Avatar from 'components/Avatar'
import { InfiniteScrollList } from 'components/InfiniteScrollList/InfiniteScrollList'
import Modal from 'components/Modal/Modal'

import './style.scss'

export const MentionableSearch = ({
  isActive,
  onClose,
  onSelect,
}: {
  isActive: boolean
  onClose: () => void
  onSelect: ({ user, type }: { user?: User; type?: 'user' | 'all' }) => void
}) => {
  const userId = useCurrentUser()?.data?.id
  const { groupId } = useParams()
  const [search, setSearch] = useState('')
  const debouncedSearchText = useDebounce(search, 300)
  const { t } = useTranslation()

  const args = useMemo(
    () => ({
      search: debouncedSearchText,
      userId,
      groupId,
    }),
    [debouncedSearchText, userId, groupId]
  )

  useEffect(() => {
    if (!isActive) {
      setSearch('')
    }
  }, [isActive])

  const List = useMemo(
    () => (
      <InfiniteScrollList
        placeholder={{
          title: t('mentionable_search.empty.title'),
          message: t('mentionable_search.empty.message'),
        }}
        query={{
          useQuery: useSearchMentionableUsersQuery,
          args,
        }}
      >
        {({ list }) => {
          return (
            <div className="MentionableSearch__list">
              {list
                ?.filter?.((x: User) => x?.id != userId)
                ?.map?.((user: User) => (
                  <a
                    key={user.id}
                    className="MentionableSearch__list__item"
                    onClick={() => {
                      onSelect({ user })
                      onClose()
                    }}
                  >
                    <Avatar user={user} />
                    <p>{user.nickname}</p>
                  </a>
                ))}
            </div>
          )
        }}
      </InfiniteScrollList>
    ),
    [args]
  )

  return (
    <Modal
      isActive={isActive}
      title={t('mentionable_search.title')}
      onClose={onClose}
      className="MentionableSearch"
    >
      <div className="MentionableSearch__input">
        <SearchIcon />
        <input
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {List}
    </Modal>
  )
}
